import { DeleteOutlined, StarOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Popconfirm, theme } from 'antd'
import { type GroupableThread, formatRowTimestamp } from '../conversationGroups'

export type ConversationListItemProps = {
  thread: GroupableThread
  active: boolean
  now: Date
  onSelect: (id: string) => void
  onDelete: (id: string) => void
}

export function ConversationListItem({
  thread,
  active,
  now,
  onSelect,
  onDelete,
}: ConversationListItemProps) {
  const { token } = theme.useToken()
  return (
    <button
      type='button'
      onClick={() => onSelect(thread.id)}
      className='gravi-conversation-row'
      style={{
        display: 'flex',
        width: '100%',
        gap: 8,
        padding: '8px 12px',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        background: active ? token.colorPrimaryBg : 'transparent',
        // Sets currentColor for the Sparkle icon and the title; a raw <button>
        // otherwise defaults to black, which is invisible in dark mode.
        color: token.colorText,
      }}
    >
      <StarOutlined
        style={{
          fontSize: 16,
          marginTop: 2,
          flexShrink: 0,
          alignSelf: 'flex-start',
          color: token.colorTextTertiary,
        }}
      />
      <Vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Texto
          weight='bold'
          className='text-ellipsis'
          style={{ maxWidth: '100%', color: token.colorText }}
        >
          {thread.title}
        </Texto>
        {thread.preview ? (
          <Texto
            className='text-ellipsis'
            style={{ maxWidth: '100%', color: token.colorTextTertiary }}
          >
            {thread.preview}
          </Texto>
        ) : null}
      </Vertical>
      <Vertical gap={2} style={{ alignItems: 'flex-end', flexShrink: 0 }}>
        <Texto style={{ color: token.colorTextTertiary, whiteSpace: 'nowrap' }}>
          {formatRowTimestamp(thread.updated_at, now)}
        </Texto>
        <Horizontal gap={4} verticalCenter>
          <Texto
            style={{ color: token.colorTextTertiary, whiteSpace: 'nowrap' }}
          >
            {`${thread.message_count} msg`}
          </Texto>
          {/* The wrapper must enclose the whole Popconfirm, not just the trigger Button: the
              confirm popup is portaled to <body>, but React events still bubble through the
              React tree (popup → Popconfirm → this row's <button>). Without stopping it here,
              clicking "OK" fires the row's onClick (onSelect) and navigates to the chat view
              instead of staying on the conversations panel. */}
          <span onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title='Delete this chat?'
              onConfirm={() => onDelete(thread.id)}
            >
              <Button
                type='text'
                size='small'
                icon={<DeleteOutlined />}
                aria-label='Delete chat'
              />
            </Popconfirm>
          </span>
        </Horizontal>
      </Vertical>
    </button>
  )
}
